import Cookies from 'js-cookie';

/**
 * Get the current domain without the 'www' prefix.
 *
 * @returns {string} The current domain.
 */
const getCurrentDomain = () => window.location.hostname.replace(/^www\./, '');

const customUrlEncodedCookies = Cookies.withConverter({
  /**
   * Custom write converter for URL encoding with lowercases encoded chars.
   *
   * @param {string} value - The value to be encoded
   * @returns {string} The encoded value
   */
  write: (value) =>
    encodeURIComponent(value).replace(/%[0-9A-F]{2}/g, (match) => match.toLowerCase()),
});

/**
 * Helper to get the language segment with the default language.
 * This will treat `example.com` and `example.com/en` as equivalent.
 *
 * Example:
 * - `example.com/en/path` is normalized to `en`
 * - `example.com` is normalized to `en`
 *
 * @param {URL} url
 * @returns {string}
 */
const getLanguageSegment = (url) => {
  const defaultLanguage = 'en';

  // Defined in assets.php.
  const languages = window.plariumSupportedLanguages;

  if (!languages?.length) {
    return defaultLanguage;
  }

  const firstSegment = url.pathname.split('/')[1];

  return languages.includes(firstSegment) ? firstSegment : defaultLanguage;
};

/**
 * Set the referrer cookie.
 * Only set if referrer is from a different language-specific path on the same domain.
 *
 * Example:
 * - Navigate from `example.com/en` to `example.com/ru` should set the cookie, even if it exists.
 * - Navigate from `example.com/en` to `example.com` should not set the cookie.
 * - Navigate from `example.com` to `example.com/en` should not set the cookie.
 */
const setCookieReferrer = () => {
  const cookieName = 'r';
  const { referrer } = document;
  const currentDomain = getCurrentDomain();

  // Exit if no referrer.
  if (!referrer) {
    return;
  }

  try {
    const referrerUrl = new URL(referrer);
    const currentUrl = new URL(window.location.href);
    const referrerLang = getLanguageSegment(referrerUrl);
    const currentLang = getLanguageSegment(currentUrl);

    // Set cookie if hostname is different or language is different.
    const shouldSetCookie =
      referrerUrl.hostname !== currentUrl.hostname || referrerLang !== currentLang;

    if (!shouldSetCookie) {
      return;
    }

    customUrlEncodedCookies.set(cookieName, referrer, {
      expires: 1,
      path: '/',
      secure: true,
      sameSite: 'None',
      domain: `.${currentDomain}`,
    });
  } catch (err) {
    // Silence is golden.
  }
};

/**
 * Set the FLP cookie.
 */
const setCookieFlp = () => {
  const cookieName = 'flp.v2';
  const currentDomain = getCurrentDomain();

  if (Cookies.get(cookieName)) {
    return;
  }

  customUrlEncodedCookies.set(cookieName, window.location.href, {
    expires: 400,
    path: '/',
    secure: true,
    sameSite: 'None',
    domain: `.${currentDomain}`,
  });
};

/**
 * Set the OID cookie.
 * Format: oid{10 digits}.{unix timestamp in seconds}
 */
const setCookieOid = () => {
  const cookieName = 'or_id.v1';
  const currentDomain = getCurrentDomain();

  if (Cookies.get(cookieName)) {
    return;
  }

  const tenDigitNumber = Math.floor(1000000000 + Math.random() * 9000000000);
  const timestamp = Math.floor(Date.now() / 1000);
  const cookieValue = `oid${tenDigitNumber}.${timestamp}`;

  Cookies.set(cookieName, cookieValue, {
    expires: 400,
    path: '/',
    secure: true,
    sameSite: 'None',
    domain: `.${currentDomain}`,
  });
};

/**
 * Set the necessary cookies.
 */
const setCookies = () => {
  setCookieReferrer();
  setCookieFlp();
  setCookieOid();
};

setCookies();
